import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProductBrand {
  id: number;
  nome?: string;
}

export interface ProductBrandFormData {
  nome?: string;
}

export function useProductBrands() {
  const queryClient = useQueryClient();

  const { data: brands = [], isLoading } = useQuery({
    queryKey: ["product-brands"],
    queryFn: async () => {
      const { data } = await apiClient.get<ProductBrand[]>("/api/marcas");
      return data;
    },
  });

  const createBrand = useMutation({
    mutationFn: async (formData: ProductBrandFormData) => {
      const { data } = await apiClient.post<ProductBrand>("/api/marcas", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-brands"] });
      toast.success("Marca criada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar marca: " + error.message);
    },
  });

  const updateBrand = useMutation({
    mutationFn: async ({ id, ...formData }: ProductBrandFormData & { id: number }) => {
      const { data } = await apiClient.put<ProductBrand>(`/api/marcas/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-brands"] });
      toast.success("Marca atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar marca: " + error.message);
    },
  });

  const deleteBrand = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/marcas/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-brands"] });
      toast.success("Marca excluída com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao excluir marca: " + error.message);
    },
  });

  return {
    brands,
    isLoading,
    createBrand,
    updateBrand,
    deleteBrand,
  };
}
